import React, { useState, useEffect } from 'react';
import { useAudioAnalyses, useFilteredStatistics } from '../hooks/useApi';
import { AudioAnalysisFilters, AudioAnalysis } from '../types';
import { formatDate, truncateText, getSentimentBadgeClass, getCategoryBadgeClass, getBooleanBadgeClass, getBooleanBadgeText } from '../utils';
import LoadingSpinner from '../components/LoadingSpinner';
import FilterPanel from '../components/FilterPanel';
import AnalysisModal from '../components/AnalysisModal';
import Pagination from '../components/Pagination';
import StatsTotalizers from '../components/StatsTotalizers';
import './AnalysesList.css';
import apiService from '../services/api';

const AnalysesList: React.FC = () => {
  const [selectedAnalysisId, setSelectedAnalysisId] = useState<number | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showTotalizers, setShowTotalizers] = useState(() => {
    // Recuperar preferência do localStorage ou usar true como padrão
    const savedPreference = localStorage.getItem('showTotalizers');
    return savedPreference !== null ? JSON.parse(savedPreference) : true;
  });
  const [downloadingIds, setDownloadingIds] = useState<Set<number>>(new Set());
  
  const {
    analyses,
    currentPage,
    totalPages,
    totalItems,
    filters,
    loading,
    error,
    applyFilters,
    clearFilters,
    goToPage,
    refresh,
    hasNextPage,
    hasPreviousPage
  } = useAudioAnalyses();

  // Hook para estatísticas filtradas
  const {
    statistics: filteredStats,
    loading: statsLoading,
    error: statsError
  } = useFilteredStatistics(filters);

  // Aplicar filtros da URL ao carregar a página
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlFilters: AudioAnalysisFilters = {};
    
    if (urlParams.get('sentiment_type')) {
      urlFilters.sentiment_type = urlParams.get('sentiment_type') as any;
    }
    if (urlParams.get('mentioned_prefecture')) {
      urlFilters.mentioned_prefecture = urlParams.get('mentioned_prefecture') === 'true';
    }
    if (urlParams.get('radio')) {
      urlFilters.radio = urlParams.get('radio') || undefined;
    }
    if (urlParams.get('client')) {
      urlFilters.client = urlParams.get('client') || undefined;
    }
    if (urlParams.get('categoria')) {
      urlFilters.categoria = urlParams.get('categoria') as any;
    }
    if (urlParams.get('termo')) {
      urlFilters.termo = urlParams.get('termo') || undefined;
    }
    if (urlParams.get('created_at_start')) {
      urlFilters.created_at_start = urlParams.get('created_at_start') || undefined;
    }
    if (urlParams.get('created_at_end')) {
      urlFilters.created_at_end = urlParams.get('created_at_end') || undefined;
    }
    
    if (Object.keys(urlFilters).length > 0) {
      applyFilters(urlFilters);
    }
  }, []); // Executar apenas uma vez na inicialização

  const handleFilterChange = (newFilters: AudioAnalysisFilters) => {
    applyFilters(newFilters);
    // Atualizar URL com os filtros
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value.toString());
      }
    });
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  };

  const handleClearFilters = () => {
    clearFilters();
    window.history.replaceState({}, '', window.location.pathname);
  };

  const handleViewAnalysis = (id: number) => {
    setSelectedAnalysisId(id);
  };

  const handleCloseModal = () => {
    setSelectedAnalysisId(null);
  };

  const handleToggleTotalizers = () => {
    const newValue = !showTotalizers;
    setShowTotalizers(newValue);
    localStorage.setItem('showTotalizers', JSON.stringify(newValue));
  };

  const handleDownload = async (analysis: AudioAnalysis) => {
    try {
      // Marcar como fazendo download
      setDownloadingIds(prev => new Set([...Array.from(prev), analysis.id]));
      
      // Gerar token de download usando downloadToken se existir, ou gerar um novo
      let downloadToken = analysis.downloadToken;
      
      if (!downloadToken) {
        // Gerar um novo token através da API
        console.log('Gerando token de download para análise:', analysis.id);
        try {
          const response = await apiService.generateDownloadToken(analysis.id);
          if (response.success && response.data?.downloadToken) {
            downloadToken = response.data.downloadToken;
          } else {
            throw new Error('Erro ao gerar token de download');
          }
        } catch (tokenError) {
          console.error('Erro ao gerar token:', tokenError);
          throw new Error('Não foi possível gerar o token de download');
        }
      }
      
      // ✅ CORRIGIR: Usar apiService.getDownloadUrl em vez de construção manual
      if (!downloadToken) {
        throw new Error('Token de download não disponível');
      }
      
      const downloadUrl = apiService.getDownloadUrl(downloadToken);
      
      console.log('🔗 Iniciando download via fetch:', downloadUrl);
      
      // Fazer requisição fetch para obter o arquivo
      const response = await fetch(downloadUrl);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro no download: ${response.status} - ${errorText}`);
      }
      
      // Verificar se o conteúdo é realmente um arquivo
      const contentLength = response.headers.get('content-length');
      const contentType = response.headers.get('content-type');
      
      console.log('📤 Headers de resposta:', {
        contentLength,
        contentType,
        contentDisposition: response.headers.get('content-disposition')
      });
      
      // Obter o blob do arquivo
      const blob = await response.blob();
      
      console.log('📊 Arquivo baixado:', {
        size: blob.size,
        type: blob.type,
        sizeInMB: (blob.size / 1024 / 1024).toFixed(2) + ' MB'
      });
      
      // Verificar se o arquivo não é muito pequeno (indicativo de erro)
      if (blob.size < 1000) {
        console.warn('⚠️ Arquivo muito pequeno, pode ser um erro:', blob.size, 'bytes');
        
        // Tentar ler o conteúdo como texto para debug
        const text = await blob.text();
        console.error('📄 Conteúdo do blob:', text.substring(0, 200));
        
        throw new Error(`Arquivo muito pequeno (${blob.size} bytes). Pode ser um erro no servidor.`);
      }
      
      // Criar URL do blob para download
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Criar link temporário para download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = analysis.file_name;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpar URL do blob
      window.URL.revokeObjectURL(blobUrl);
      
      console.log('✅ Download concluído para:', analysis.file_name);
      
    } catch (error: any) {
      console.error('❌ Erro ao fazer download:', error);
      alert(`Erro ao fazer download do arquivo: ${error.message || 'Erro desconhecido'}`);
    } finally {
      // Remover do estado de downloading
      setDownloadingIds(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(analysis.id);
        return newSet;
      });
    }
  };

  if (loading && analyses.length === 0) {
    return <LoadingSpinner message="Carregando análises..." />;
  }

  if (error) {
    return (
      <div className="analyses-error">
        <div className="error-content">
          <h2>Erro ao carregar análises</h2>
          <p>{error}</p>
          <button onClick={refresh} className="retry-button">
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="analyses-list">
      <div className="analyses-header">
        <div className="header-left">
          <h1>Análises de Áudio</h1>
          <p>Total: {totalItems.toLocaleString()} análises</p>
        </div>
        <div className="header-actions">
          <button
            onClick={handleToggleTotalizers}
            className={`totalizers-toggle ${showTotalizers ? 'active' : ''}`}
            title={showTotalizers ? 'Ocultar totalizadores' : 'Exibir totalizadores'}
          >
            📊 {showTotalizers ? 'Ocultar' : 'Exibir'} Totais
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`filter-toggle ${showFilters ? 'active' : ''}`}
          >
            🔍 Filtros
          </button>
          <button onClick={refresh} className="refresh-button" disabled={loading}>
            {loading ? '⏳' : '🔄'} Atualizar
          </button>
        </div>
      </div>

      {/* Painel de filtros */}
      <FilterPanel
        isOpen={showFilters}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />

      {/* Estatísticas totais - condicionalmente renderizado */}
      {showTotalizers && (
        <StatsTotalizers
          statistics={filteredStats}
          loading={statsLoading}
          error={statsError}
        />
      )}

      {/* Lista de análises */}
      <div className="analyses-content">
        {analyses.length === 0 ? (
          <div className="empty-state">
            <h3>Nenhuma análise encontrada</h3>
            <p>Tente ajustar os filtros ou verificar se há dados disponíveis.</p>
          </div>
        ) : (
          <>
            <div className="analyses-grid">
              {analyses.map((analysis) => (
                <div key={analysis.id} className="analysis-card">
                  <div className="analysis-header">
                    <div className="analysis-meta">
                      <span className="analysis-id">#{analysis.id}</span>
                      <span className="analysis-date">
                        {formatDate(analysis.created_at)}
                      </span>
                    </div>
                    <div className="analysis-badges">
                      <span className={`badge ${getSentimentBadgeClass(analysis.sentiment_type)}`}>
                        {analysis.sentiment_type}
                      </span>
                      {analysis.categoria && (
                        <span className={`badge ${getCategoryBadgeClass(analysis.categoria)}`}>
                          {analysis.categoria}
                        </span>
                      )}
                      {analysis.mentioned_prefecture && (
                        <span className="badge badge-prefecture">
                          🏛️ Prefeitura
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="analysis-content">
                    <h3 className="analysis-radio">{analysis.radio}</h3>
                    <p className="analysis-client">Cliente: {analysis.client}</p>
                    <p className="analysis-text">
                      {truncateText(analysis.content, 150)}
                    </p>
                    {analysis.termo && (
                      <p className="analysis-term">
                        <strong>Termo:</strong> {analysis.termo}
                      </p>
                    )}
                  </div>

                  <div className="analysis-footer">
                    <div className="analysis-info">
                      <span className="analysis-model">
                        IA: {analysis.ai_model}
                      </span>
                      <span className="analysis-email">
                        Email: {getBooleanBadgeText(!!analysis.email_sent_at)}
                      </span>
                    </div>
                    <div className="analysis-actions">
                      <button
                        onClick={() => handleDownload(analysis)}
                        className={`download-button ${downloadingIds.has(analysis.id) ? 'downloading' : ''}`}
                        disabled={downloadingIds.has(analysis.id)}
                        title={
                          downloadingIds.has(analysis.id) 
                            ? 'Baixando...' 
                            : 'Baixar áudio'
                        }
                      >
                        {downloadingIds.has(analysis.id) ? (
                          <>⏳ Baixando...</>
                        ) : (
                          <>📥 Download</>
                        )}
                      </button>
                      <button
                        onClick={() => handleViewAnalysis(analysis.id)}
                        className="view-button"
                      >
                        Ver detalhes
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Paginação */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={20}
              onPageChange={goToPage}
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
            />
          </>
        )}
      </div>

      {/* Modal de detalhes */}
      {selectedAnalysisId && (
        <AnalysisModal
          analysisId={selectedAnalysisId}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default AnalysesList;
